import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import { AssetField } from '../scene/fields/AssetField.js';
import { IconButton, NumberField, PropRow, SelectField, Toggle, VecField } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import type {
  BrushFalloff,
  BrushShapeJson,
  StampBrushJson,
  TerrainBrush,
  TerrainLayerJson,
  TreeBrushJson,
  VoxelBrushShape,
} from './terrain-store.js';
import {
  BUILTIN_STAMPS,
  FALLOFF_PRESETS,
  falloffPath,
  presetPoints,
  SIDES,
  STAMP_BLENDS,
  stampValue,
  TREE_SPEC,
  type FalloffPreset,
  type Side,
} from './terrain-tools.js';
import './terrain-tools.css';

const PNG_SPEC = { suffix: '.png', label: 'Image' } as const;

// ---------------------------------------------------------------- falloff curve

const CW = 180;
const CH = 72;

/**
 * Small SVG curve editor for a brush falloff (center on the left, edge on
 * the right). Drag points; click empty space to add one; double-click a
 * point to remove it (at least two stay).
 */
export function FalloffCurveEditor({ points, onChange }: { points: Array<[number, number]>; onChange: (points: Array<[number, number]>) => void }) {
  const svg = useRef<SVGSVGElement | null>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const sorted = points.slice().sort((a, b) => a[0] - b[0]);
  const toCurve = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = svg.current!.getBoundingClientRect();
    const t = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const v = Math.min(1, Math.max(0, 1 - (e.clientY - r.top) / r.height));
    return [Math.round(t * 100) / 100, Math.round(v * 100) / 100];
  };
  const move = (e: ReactPointerEvent) => {
    if (drag === null || !svg.current) return;
    const next = sorted.slice();
    next[drag] = toCurve(e);
    onChange(next);
  };
  return (
    <svg
      ref={svg}
      className="terrain-curve"
      viewBox={`0 0 ${CW} ${CH}`}
      role="img"
      aria-label="Falloff curve"
      onPointerMove={move}
      onPointerUp={() => setDrag(null)}
      onPointerLeave={() => setDrag(null)}
      onPointerDown={(e) => {
        if (e.target !== svg.current) return;
        onChange([...sorted, toCurve(e)].sort((a, b) => a[0] - b[0]));
      }}
    >
      <polyline className="terrain-curve-line" points={falloffPath(sorted, CW, CH)} />
      {sorted.map((p, i) => (
        <circle
          key={i}
          className="terrain-curve-point"
          cx={p[0] * CW}
          cy={(1 - p[1]) * CH}
          r={4}
          onPointerDown={(e) => {
            e.stopPropagation();
            (e.target as Element).setPointerCapture?.(e.pointerId);
            setDrag(i);
          }}
          onDoubleClick={() => sorted.length > 2 && onChange(sorted.filter((_, j) => j !== i))}
        />
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------- brush shape

/** Falloff, image mask, rotation, random rotation and spacing of the brush. */
export function BrushShapePanel({ brush, setBrush }: { brush: TerrainBrush; setBrush: (patch: Partial<TerrainBrush>) => void }) {
  const shape: BrushShapeJson = brush.shape ?? {};
  const setShape = (patch: Partial<BrushShapeJson>) => {
    const next: BrushShapeJson = { ...shape, ...patch };
    for (const k of Object.keys(next) as Array<keyof BrushShapeJson>) if (next[k] === undefined) delete next[k];
    setBrush({ shape: Object.keys(next).length ? next : undefined });
  };
  const falloff = shape.falloff;
  const mode = falloff === undefined ? '' : Array.isArray(falloff) ? 'custom' : falloff;
  return (
    <>
      <PropRow label="Falloff" hint="Strength from the brush center (left) to its edge (right). Default = the tool's own">
        <SelectField
          value={mode}
          options={[{ value: '', label: 'Tool default' }, ...FALLOFF_PRESETS.map((p) => ({ value: p, label: p })), { value: 'custom', label: 'Custom curve' }]}
          onChange={(v) =>
            setShape({
              falloff: v === '' ? undefined : v === 'custom' ? presetPoints(typeof falloff === 'string' ? (falloff as FalloffPreset) : 'smooth') : (v as BrushFalloff),
            })
          }
        />
      </PropRow>
      {falloff !== undefined && (
        <div className="terrain-curve-row">
          <FalloffCurveEditor
            points={Array.isArray(falloff) ? falloff : presetPoints(falloff as FalloffPreset, 9)}
            onChange={(points) => setShape({ falloff: points })}
          />
        </div>
      )}
      <PropRow label="Brush mask" hint="Grayscale PNG used as the brush shape (white = full strength)">
        <AssetField spec={PNG_SPEC} value={shape.mask} onChange={(mask) => setShape({ mask: mask || undefined })} />
      </PropRow>
      {shape.mask && (
        <>
          <PropRow label="Mask rotation" hint="Degrees">
            <NumberField value={shape.rotation ?? 0} step={5} onChange={(rotation) => setShape({ rotation: rotation || undefined })} />
          </PropRow>
          <PropRow label="Random rotation" hint="Turn the mask randomly for every dab">
            <Toggle checked={brush.randomRotation} onChange={(randomRotation) => setBrush({ randomRotation })} />
          </PropRow>
        </>
      )}
      <PropRow label="Spacing" hint="Distance between dabs along a stroke, as a fraction of the radius (0 = continuous while held)">
        <NumberField value={brush.spacing} step={0.05} min={0} max={4} onChange={(spacing) => setBrush({ spacing })} />
      </PropRow>
    </>
  );
}

// ---------------------------------------------------------------- voxel brush

const VOXEL_SHAPES: Array<{ value: VoxelBrushShape; label: string }> = [
  { value: 'sphere', label: 'Sphere' },
  { value: 'box', label: 'Box' },
];

/** Falloff and volume of the voxel tools' dabs. */
export function VoxelBrushPanel({ brush, setBrush }: { brush: TerrainBrush; setBrush: (patch: Partial<TerrainBrush>) => void }) {
  const falloff = brush.falloff ?? 0.35;
  return (
    <>
      <PropRow label="Falloff" hint="0..1: share of the radius over which the brush blends softly into the ground (0 = hard edge)">
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={falloff}
          aria-label="Falloff"
          style={{ flex: 2, minWidth: 0 }}
          onChange={(e) => setBrush({ falloff: Number(e.target.value) })}
        />
        <NumberField value={falloff} step={0.05} min={0} max={1} onChange={(v) => setBrush({ falloff: v })} />
      </PropRow>
      <PropRow label="Shape" hint="Volume of each dab: a sphere, or a box with half-size = radius">
        <SelectField value={brush.voxelShape ?? 'sphere'} options={VOXEL_SHAPES} onChange={(v) => setBrush({ voxelShape: v as VoxelBrushShape })} />
      </PropRow>
    </>
  );
}

/** CSS color of a layer tint (sRGB 0..1). */
function layerColor(l: TerrainLayerJson): string {
  const c = Array.isArray(l.color) ? l.color : [1, 1, 1];
  const ch = (v: number | undefined) => Math.round(Math.min(1, Math.max(0, v ?? 1)) * 255);
  return `rgb(${ch(c[0])}, ${ch(c[1])}, ${ch(c[2])})`;
}

/**
 * Voxel material palette: one swatch per layer (tint, material number =
 * index + 1, name); picking one sets the brush layer.
 */
export function VoxelMaterialPalette({ layers, selected, onSelect }: { layers: TerrainLayerJson[]; selected: number; onSelect: (layer: number) => void }) {
  const current = Math.min(selected, layers.length - 1);
  return (
    <div className="terrain-stamp-list" role="listbox" aria-label="Voxel materials">
      {layers.map((l, i) => {
        const name = l.name || `layer ${i + 1}`;
        return (
          <button
            key={i}
            type="button"
            role="option"
            aria-selected={i === current}
            className={`small ${i === current ? 'primary' : 'ghost'}`}
            title={`Material ${i + 1}: ${name}${l.diggable === false ? ' (not diggable)' : ''}`}
            onClick={() => onSelect(i)}
          >
            <span className="swatch" style={{ background: layerColor(l) }} />
            <span className="mono">{i + 1}</span> {name}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- stamp

/** Height preview of a built-in stamp (grayscale cells, crater bowls blue). */
function StampPreview({ stamp, rotation }: { stamp: string; rotation: number }) {
  const n = 16;
  const a = (rotation * Math.PI) / 180;
  const cells = [];
  for (let j = 0; j < n; j += 1) {
    for (let i = 0; i < n; i += 1) {
      const x = ((i + 0.5) / n) * 2 - 1;
      const z = ((j + 0.5) / n) * 2 - 1;
      const u = x * Math.cos(a) + z * Math.sin(a);
      const w = -x * Math.sin(a) + z * Math.cos(a);
      const v = Math.abs(u) > 1 || Math.abs(w) > 1 ? 0 : stampValue(stamp, u, w);
      const c = Math.round(Math.abs(v) * 255);
      cells.push(<rect key={`${i}-${j}`} x={i} y={n - 1 - j} width={1} height={1} fill={v < 0 ? `rgb(30,60,${c})` : `rgb(${c},${c},${c})`} />);
    }
  }
  return (
    <svg className="terrain-stamp-preview" viewBox={`0 0 ${n} ${n}`} role="img" aria-label={`${stamp} stamp`}>
      {cells}
    </svg>
  );
}

export function StampPanel({ stamp, onChange }: { stamp: StampBrushJson; onChange: (stamp: StampBrushJson) => void }) {
  const set = (patch: Partial<StampBrushJson>) => onChange({ ...stamp, ...patch });
  return (
    <>
      <div className="terrain-stamp-row">
        {!stamp.image && <StampPreview stamp={stamp.stamp} rotation={stamp.rotation} />}
        <div className="terrain-stamp-list" role="listbox" aria-label="Stamps">
          {BUILTIN_STAMPS.map((s) => (
            <button key={s} type="button" className={`small ${!stamp.image && stamp.stamp === s ? 'primary' : 'ghost'}`} onClick={() => set({ stamp: s, image: undefined })}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <PropRow label="Image" hint="Grayscale PNG stamp (overrides the built-in shape)">
        <AssetField spec={PNG_SPEC} value={stamp.image} onChange={(image) => set({ image: image || undefined })} />
      </PropRow>
      <PropRow label="Size" hint="Meters across">
        <NumberField value={stamp.size} step={1} min={0.5} onChange={(size) => set({ size })} />
      </PropRow>
      <PropRow label="Height" hint="Meters (Shift-click inverts)">
        <NumberField value={stamp.height} step={0.5} onChange={(height) => set({ height })} />
      </PropRow>
      <PropRow label="Rotation" hint="Degrees">
        <NumberField value={stamp.rotation} step={5} onChange={(rotation) => set({ rotation })} />
      </PropRow>
      <PropRow label="Blend" hint="add: on top · max: raise to the stamp · min: cut down to it · set: replace (max/min/set sit on the clicked height)">
        <SelectField value={stamp.blend} options={STAMP_BLENDS} onChange={(blend) => set({ blend: blend as StampBrushJson['blend'] })} />
      </PropRow>
      <PropRow label="Edge falloff" hint="Outer fraction that fades out (0..1)">
        <NumberField value={stamp.falloff} step={0.05} min={0} max={1} onChange={(falloff) => set({ falloff })} />
      </PropRow>
      <PropRow label="Opacity" hint="0..1">
        <NumberField value={stamp.strength} step={0.05} min={0} max={1} onChange={(strength) => set({ strength })} />
      </PropRow>
    </>
  );
}

// ---------------------------------------------------------------- trees

export function TreesPanel({ trees, onChange }: { trees: TreeBrushJson; onChange: (trees: TreeBrushJson) => void }) {
  const set = (patch: Partial<TreeBrushJson>) => onChange({ ...trees, ...patch });
  const protos = trees.prototypes;
  return (
    <>
      <div className="tilemap-row">
        <span className="muted">Tree prototypes</span>
        <span className="bar-spacer" />
        <button type="button" className="ghost small" onClick={() => set({ prototypes: [...protos, { source: '', weight: 1 }] })}>
          <Icon name="plus" size={12} /> Tree
        </button>
      </div>
      {protos.length === 0 && <p className="muted tilemap-hint">Add a prefab or model to paint. Trees become scene entities under a “Trees” group of this terrain.</p>}
      {protos.map((p, i) => (
        <div key={i} className="terrain-proto">
          <AssetField
            spec={TREE_SPEC}
            value={p.source || undefined}
            onChange={(source) => set({ prototypes: protos.map((q, j) => (j === i ? { ...q, source: source ?? '' } : q)) })}
          />
          <NumberField value={p.weight} step={0.1} min={0} title="Relative weight" onChange={(weight) => set({ prototypes: protos.map((q, j) => (j === i ? { ...q, weight } : q)) })} />
          <IconButton icon="trash" title="Remove prototype" size={12} onClick={() => set({ prototypes: protos.filter((_, j) => j !== i) })} />
        </div>
      ))}
      <PropRow label="Density" hint="Trees per 100 m² per dab">
        <NumberField value={trees.density} step={0.25} min={0} onChange={(density) => set({ density })} />
      </PropRow>
      <PropRow label="Min distance" hint="Meters between trees (also to existing ones)">
        <NumberField value={trees.minDistance} step={0.5} min={0} onChange={(minDistance) => set({ minDistance })} />
      </PropRow>
      <PropRow label="Scale" hint="Random uniform scale range">
        <VecField size={2} step={0.05} labels={['min', 'max']} value={trees.scale} onChange={(v) => set({ scale: [v[0] ?? 1, v[1] ?? 1] })} />
      </PropRow>
      <PropRow label="Rotation" hint="Random yaw range (degrees)">
        <VecField size={2} step={5} labels={['min', 'max']} value={trees.rotation} onChange={(v) => set({ rotation: [v[0] ?? 0, v[1] ?? 360] })} />
      </PropRow>
      <PropRow label="Align to ground" hint="Tilt trees to the ground normal">
        <Toggle checked={trees.alignToNormal} onChange={(alignToNormal) => set({ alignToNormal })} />
      </PropRow>
    </>
  );
}

// ---------------------------------------------------------------- neighbours

const ARROWS: Record<Side, string> = { left: '←', right: '→', back: '↓', front: '↑' };

/**
 * 3 × 3 mini map around this tile (+Z up): existing tiles by id, a create
 * button on each free side, plus Stitch and auto-stitch.
 */
export function NeighborsPanel({
  grid,
  self,
  linked,
  autoStitch,
  onCreate,
  onStitch,
  onAutoStitch,
}: {
  grid: Array<{ dx: number; dz: number; id: string | null }>;
  self: string;
  linked: Partial<Record<Side, string>>;
  autoStitch: boolean;
  onCreate: (side: Side) => void;
  onStitch: () => void;
  onAutoStitch: (on: boolean) => void;
}) {
  const sideOf = (dx: number, dz: number): Side | null => SIDES.find((s) => ({ left: [-1, 0], right: [1, 0], back: [0, -1], front: [0, 1] })[s].join() === `${dx},${dz}`) ?? null;
  return (
    <>
      <div className="terrain-neighbors" role="grid" aria-label="Neighbouring tiles">
        {grid.map((c) => {
          const side = sideOf(c.dx, c.dz);
          const center = c.dx === 0 && c.dz === 0;
          if (center) return <div key="c" className="terrain-tile is-self" title={self}>{self}</div>;
          if (c.id) {
            const isLinked = side ? linked[side] === c.id : false;
            return (
              <div key={`${c.dx},${c.dz}`} className={`terrain-tile${isLinked ? ' is-linked' : ''}`} title={isLinked ? `${c.id} (linked)` : c.id}>
                {c.id}
              </div>
            );
          }
          if (!side) return <div key={`${c.dx},${c.dz}`} className="terrain-tile is-empty" />;
          return (
            <button key={side} type="button" className="terrain-tile is-add ghost" title={`Create the ${side} neighbour`} onClick={() => onCreate(side)}>
              {ARROWS[side]} +
            </button>
          );
        })}
      </div>
      <div className="tilemap-row">
        <Toggle checked={autoStitch} label="Auto-stitch" onChange={onAutoStitch} />
        <span className="bar-spacer" />
        <button type="button" className="small ghost" title="Average the shared borders with the linked neighbours" disabled={Object.keys(linked).length === 0} onClick={onStitch}>
          Stitch
        </button>
      </div>
    </>
  );
}
